import type { ReactNode } from 'react';

import { Box, chakra, HStack, Text } from '@chakra-ui/react';
import { ArrowBack, Close } from '@m/icons';
import { Drawer } from 'vaul';

import { NavigationButton } from '../navigation-button';
import { typographyMixins } from '../typography';

export interface BottomSheetHeaderProps {
  children?: ReactNode;
  flush?: boolean;
  onBack?: () => void;
  onClose?: () => void;
  showBackButton?: boolean;
  showCloseButton?: boolean;
  showHandle?: boolean;
  subtitle?: ReactNode;
  title?: ReactNode;
  titleAlign?: 'center' | 'left';
}

const sideSlotSize = '48px';
const closeOnlyHeight = '64px';

const DrawerTitle = chakra(Drawer.Title, {
  base: {
    ...typographyMixins.Subheader,
    color: 'text.primary',
    fontWeight: '600',
    m: 0,
  },
});

const GenericDrawerHandle = chakra(Drawer.Handle, {
  base: {
    h: '1',
    w: '12',
  },
});

export const BottomSheetHeader = ({
  children,
  flush = false,
  onBack,
  onClose,
  showBackButton = false,
  showCloseButton = false,
  showHandle = false,
  subtitle,
  title,
  titleAlign = 'left',
}: BottomSheetHeaderProps) => {
  const hasTextHeader = Boolean(title || subtitle);
  const hasContentHeader = Boolean(
    children || hasTextHeader || showCloseButton || showBackButton || showHandle,
  );
  const closeOnlyHeader =
    !children && !hasTextHeader && !showCloseButton && !showBackButton && !showHandle;
  const shouldBalanceCenteredClose =
    titleAlign === 'center' &&
    showCloseButton &&
    Boolean(title) &&
    !subtitle &&
    !children &&
    !showBackButton;
  const paddingX = flush ? 0 : 4;
  const paddingTop = flush ? 0 : 4;
  const showClose = closeOnlyHeader || showCloseButton;

  if (!closeOnlyHeader && !hasContentHeader) {
    return null;
  }

  return (
    <Box
      display="flex"
      flexDirection="column"
      flexShrink={0}
      gap={closeOnlyHeader ? undefined : 2}
      h={closeOnlyHeader ? closeOnlyHeight : undefined}
      overflow={closeOnlyHeader ? 'hidden' : undefined}
      position={closeOnlyHeader ? 'relative' : undefined}
      px={closeOnlyHeader ? undefined : paddingX}
      pt={closeOnlyHeader ? undefined : paddingTop}
      width="100%"
    >
      {!closeOnlyHeader && showHandle && (
        <Box display="flex" justifyContent="center" mb={flush ? 3 : 0} mt={flush ? 4 : 0}>
          <GenericDrawerHandle />
        </Box>
      )}
      {(closeOnlyHeader || children || hasTextHeader || showCloseButton || showBackButton) && (
        <HStack
          align="center"
          flex={closeOnlyHeader ? 1 : undefined}
          gap={2}
          justify={closeOnlyHeader ? 'flex-end' : 'space-between'}
          minH={closeOnlyHeader ? 0 : undefined}
          px={closeOnlyHeader ? 2 : undefined}
          width="100%"
        >
          {showBackButton ? (
            <NavigationButton aria-label="Go back" onClick={onBack} disabled={!onBack}>
              <ArrowBack />
            </NavigationButton>
          ) : (
            (closeOnlyHeader || shouldBalanceCenteredClose) && (
              <Box aria-hidden="true" flexShrink={0} height={sideSlotSize} width={sideSlotSize} />
            )
          )}
          {!closeOnlyHeader && (
            <Box
              alignItems={titleAlign === 'center' ? 'center' : 'flex-start'}
              display="flex"
              flex={1}
              flexDirection="column"
              justifyContent={titleAlign === 'center' ? 'center' : 'flex-start'}
              minW={0}
              textAlign={titleAlign}
            >
              {children}
              {!children && hasTextHeader && (
                <Box display="flex" flexDirection="column" gap="1" width="100%">
                  {title && <DrawerTitle>{title}</DrawerTitle>}
                  {subtitle && (
                    <Text variant="SmallBodyRegular" color="text.secondary">
                      {subtitle}
                    </Text>
                  )}
                </Box>
              )}
            </Box>
          )}
          {showClose && (
            <NavigationButton aria-label="Close" onClick={onClose} disabled={!onClose}>
              <Close />
            </NavigationButton>
          )}
        </HStack>
      )}
    </Box>
  );
};

export default BottomSheetHeader;
